<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcCompanyBankAccounts',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parent: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const items = computed(() => {
    return props.parent.bank_accounts.data;
  });

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );

  const act_2095089031469157_showModal_nd891mhil_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CompanyBankAccount',
      props: {
        parent: props.parent,
      },
    };
  };
  const act_294572930540096_showModal_9o8df9r3a_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CompanyBankAccount',
      props: {
        parent: props.parent,
        item: $event.scope.item,
      },
    };
  };
  const act_3365375699802249_rest_a9xm26wzm_getActionArgs = async ($event) => {
    return {
      method: 'delete',
      container: props.parent.bank_accounts,
      resource: $event.scope.item,
    };
  };
  const act_3365375699802249_rest_a9xm26wzm_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_7315092382398562_reloadComponentData_9fluw6kl9 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_sl1wf04xx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_9fluw6kl9,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_sl1wf04xx],
    event: 'reload',
  });
  const act_2095089031469157_showModal_nd891mhil =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_nd891mhil_getActionArgs,
      displayName: 'create',
    });
  const inst_evh_2095089031469157_clickPreventStop_4bggr5mlo =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_nd891mhil,
      event: 'click.prevent.stop',
      displayName: 'create',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_4bggr5mlo],
      event: 'click.prevent.stop',
    });
  const act_294572930540096_showModal_9o8df9r3a =
    new actionTypes.ShowModalAction({
      actionArgs: act_294572930540096_showModal_9o8df9r3a_getActionArgs,
    });
  const inst_evh_294572930540096_clickPreventStop_voytnkf4e =
    new actionTypes.EventHandlerImpl({
      action: act_294572930540096_showModal_9o8df9r3a,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_294572930540096_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_294572930540096_clickPreventStop_voytnkf4e],
      event: 'click.prevent.stop',
    });
  const act_3365375699802249_rest_a9xm26wzm = new actionTypes.RestAction({
    actionArgs: act_3365375699802249_rest_a9xm26wzm_getActionArgs,
    confirm: act_3365375699802249_rest_a9xm26wzm_getConfirm,
  });
  const inst_evh_3365375699802249_clickPreventStop_lqzzs5vu3 =
    new actionTypes.EventHandlerImpl({
      action: act_3365375699802249_rest_a9xm26wzm,
      event: 'click.prevent.stop',
      displayName: 'rest',
    });
  const inst_evh_3365375699802249_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3365375699802249_clickPreventStop_lqzzs5vu3],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_sl1wf04xx,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_clickPreventStop_4bggr5mlo,
    create: inst_evh_2095089031469157_clickPreventStop_4bggr5mlo,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_294572930540096_clickPreventStop_voytnkf4e,
    inst_evh_294572930540096_clickPreventStop,
    inst_evh_3365375699802249_clickPreventStop_lqzzs5vu3,
    inst_evh_3365375699802249_clickPreventStop,
  };

  const evh_7315092382398562_reload_sl1wf04xx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_sl1wf04xx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_4bggr5mlo = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_4bggr5mlo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop_voytnkf4e = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_294572930540096_clickPreventStop_voytnkf4e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_294572930540096_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop_lqzzs5vu3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop_lqzzs5vu3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('CompanyBankAccounts', 'Bank accounts') }}
          </strong>
          <ItpButton
            :text="gettext('Add bank account...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!parent.isReadOnly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!items || !items.length">
          <span>
            {{ gettext('No bank accounts have been added yet.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex flex-column"
          v-for="item in _.orderBy(items, ['created_at'], ['desc'])"
        >
          <ItpBox spacing="pb-1">
            <UcCompanyBankAccountCard :item="item" class="mb-2">
            </UcCompanyBankAccountCard>
            <BDropdown
              size="sm"
              toggle-class="btn-default-borderless"
              no-caret
              variant="none"
              class="ms-auto mb-auto"
              v-if="!item.locked"
            >
              <template #button-content>
                <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
              </template>
              <BDropdownItem
                @click.prevent.stop="
                  evh_294572930540096_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
              </BDropdownItem>
              <BDropdownItem
                v-if="!item.locked"
                @click.prevent.stop="
                  evh_3365375699802249_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
              </BDropdownItem>
            </BDropdown>
          </ItpBox>
          <div class="d-flex align-items-end">
            <ItpText muted small>
              {{ datetime(item.created_at) }}
            </ItpText>
            <small class="text-muted text-nowrap mx-2">
              {{ username(item.updated_by || item.created_by) }}
            </small>
            <div
              class="ms-2 d-flex align-self-end flex-grow-1 justify-content-end"
            >
              <ItpIcon
                :title="gettext('Locked')"
                name="fa-lock"
                class="text-muted"
                v-if="item.locked"
              >
              </ItpIcon>
            </div>
          </div>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
